import { useState, useEffect, useRef } from 'react';
import { books, galleryImages, categoryCards } from '../../mocks/books';
import { upcomingEvents, pastEvents } from '../../mocks/events';
import { publishingStats, testimonials, publishingProcess } from '../../mocks/homeSections';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useLatestVideos } from '../../hooks/useLatestVideos';
import { useLatestBook } from '../../hooks/useLatestBook';
import { useFeaturedBook } from '../../hooks/useFeaturedBook';
import DistributionMarquee from '@/pages/home/components/DistributionMarquee';
import PartnerStatement from '@/pages/home/components/PartnerStatement';
import CredentialsStrip from '@/pages/home/components/CredentialsStrip';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';

function AnimatedCounter({ target, suffix, duration = 2000 }: { target: number; suffix: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Home() {
  const [currentBook, setCurrentBook] = useState(0);
  const [currentGallery, setCurrentGallery] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const { videos: latestVideos, loading: videosLoading, error: videosError } = useLatestVideos(3);
  const { book: latestBook, loading: bookLoading } = useLatestBook();
  const featured = useFeaturedBook();

  const recommendedBooks = (() => {
    const merged = [...books];
    if (latestBook && !bookLoading) {
      const isDuplicate = merged.some(
        (b) => b.link === latestBook.link || b.title.toLowerCase() === latestBook.title.toLowerCase()
      );
      if (!isDuplicate) {
        merged.unshift({
          id: 0,
          title: latestBook.title,
          author: latestBook.author,
          description: latestBook.description,
          cover: latestBook.cover,
          link: latestBook.link,
          isLatest: true,
        });
      }
    }
    return merged;
  })();
  const galleryPerPage = 3;

  const displayVideos = latestVideos;
  const videoSlides = 1; // Always show all 3 latest videos in one slide

  const bookSlides = Math.ceil(recommendedBooks.length / 3);
  const gallerySlides = Math.ceil(galleryImages.length / galleryPerPage);

  const nextBook = () => setCurrentBook((prev) => (prev + 1) % bookSlides);
  const prevBook = () => setCurrentBook((prev) => (prev - 1 + bookSlides) % bookSlides);
  const nextGallery = () => setCurrentGallery((prev) => (prev + 1) % gallerySlides);
  const prevGallery = () => setCurrentGallery((prev) => (prev - 1 + gallerySlides) % gallerySlides);
  const nextTestimonial = () => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  // Scroll animations
  const heroAnim = useScrollAnimation({ threshold: 0.1 });
  const featuredAnim = useScrollAnimation({ threshold: 0.15 });
  const browseAnim = useScrollAnimation({ threshold: 0.2 });
  const videoAnim = useScrollAnimation({ threshold: 0.1 });
  const recommendAnim = useScrollAnimation({ threshold: 0.1 });
  const categoryAnim = useScrollAnimation({ threshold: 0.1 });
  const testimonialAnim = useScrollAnimation({ threshold: 0.2 });
  const processAnim = useScrollAnimation({ threshold: 0.1 });
  const dealsAnim = useScrollAnimation({ threshold: 0.2 });
  const galleryAnim = useScrollAnimation({ threshold: 0.1 });
  const engageAnim = useScrollAnimation({ threshold: 0.15 });
  const bestsellerAnim = useScrollAnimation({ threshold: 0.15 });
  const paymentAnim = useScrollAnimation({ threshold: 0.3 });

  return (
    <main>
      <Seo {...pageSeo.home} />
      {/* ============ HERO SECTION ============ */}
      <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20golden%20amber%20soft%20abstract%20background%20with%20subtle%20floating%20book%20page%20textures%2C%20cream%20and%20warm%20ivory%20tones%2C%20ethereal%20literary%20atmosphere%2C%20elegant%20minimal%20composition%2C%20soft%20defocused%20ambient%20lighting%2C%20premium%20aesthetic%2C%20gentle%20gradient%20from%20warm%20cream%20to%20soft%20tawny%2C%20no%20harsh%20edges%2C%20dreamy%20atmospheric%20quality&width=1920&height=900&seq=leavitt-hero-bg-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/75 via-navy-900/60 to-navy-900/80"></div>
        </div>

        {/* Content */}
        <div ref={heroAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 py-16 md:py-20 animate-on-scroll ${heroAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-6xl mx-auto">
            {/* Left - Text */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/20 border border-gold-400/30 rounded-full mb-6">
                <div className="w-2 h-2 rounded-full bg-gold-400 animate-pulse"></div>
                <span className="text-xs font-medium text-gold-300 uppercase tracking-widest">Featured Book of the Month</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
                Discover Your Next
                <br />
                <span className="text-gold-400">Great Read</span>
              </h1>
              <p className="text-base md:text-lg text-cream-200 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                Explore our curated collection of exceptional books from talented authors. From gripping fiction to transformative self-help, find the story that speaks to you.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <a
                  href={featured.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
                >
                  Explore This Month's Pick
                </a>
                <a
                  href="https://www.amazon.com/s?k=leavitt+peak+press"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Browse All Books
                </a>
              </div>
            </div>

            {/* Right - Featured Book */}
            <div className="flex-1 flex justify-center lg:justify-end">
              <div className="relative animate-float">
                <div className="absolute -inset-4 bg-gold-400/20 rounded-3xl blur-2xl animate-pulse"></div>
                <div className="relative w-56 md:w-64 lg:w-72">
                  <img
                    src={featured.cover}
                    alt={featured.title}
                    className="w-full h-auto rounded-xl shadow-2xl shadow-navy-950/50 transition-transform duration-500 hover:scale-105 hover:rotate-[-2deg]"
                  />
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white rounded-lg px-4 py-2 shadow-lg whitespace-nowrap">
                    <p className="text-xs font-semibold text-navy-900">{featured.title}</p>
                    <p className="text-xs text-navy-500">{featured.author}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gradient transition */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* ============ STATS COUNTER STRIP ============ */}
      <section className="w-full py-10 md:py-14 bg-white -mt-1 relative z-10">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
            {publishingStats.map((stat, idx) => (
              <div key={stat.label} className="text-center group" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-gold-100 text-gold-600 transition-all duration-500 group-hover:bg-gold-500 group-hover:text-white group-hover:scale-110 group-hover:rotate-6">
                  <i className={`${stat.icon} text-xl`}></i>
                </div>
                <div className="text-3xl md:text-4xl font-bold text-navy-900 mb-1">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-sm text-navy-500 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AWARD SEAL & BBB A+ CREDENTIALS ============ */}
      <CredentialsStrip />

      {/* ============ FEATURED BOOK OF THE MONTH ============ */}
      <section className="w-full py-14 md:py-20 bg-cream-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={featuredAnim.ref} className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-6xl mx-auto animate-on-scroll ${featuredAnim.isVisible ? 'animate-visible' : ''}`}>
            {/* Image */}
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold-200/50 rounded-full blur-xl"></div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-navy-200/30 rounded-full blur-xl"></div>
                <div className="relative w-64 md:w-72 lg:w-80">
                  <img
                    src={featured.cover}
                    alt={featured.title}
                    className="w-full h-auto rounded-xl shadow-lg transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white rounded-lg px-4 py-2 shadow-lg whitespace-nowrap">
                    <p className="text-xs font-semibold text-navy-900">{featured.title}</p>
                    <p className="text-xs text-navy-500">{featured.author}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/overline.svg"
                  alt=""
                  className="h-4 w-auto"
                />
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-2">
                {featured.title}
              </h2>
              {featured.subtitle && (
                <p className="text-sm font-medium text-gold-600 mb-1">{featured.subtitle}</p>
              )}
              <p className="text-sm text-navy-500 mb-4">by <strong>{featured.author}</strong></p>
              <div className="w-16 h-1 bg-gold-400 rounded-full mb-6"></div>
              <div className="text-sm md:text-base text-navy-700 leading-relaxed mb-6">
                {featured.paragraphs.map((paragraph, idx) => (
                  <p key={idx} className={idx < featured.paragraphs.length - 1 ? 'mb-3' : ''}>
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <a
                  href={featured.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Get This Book on Amazon
                </a>
                <a
                  href="https://www.amazon.com/s?k=leavitt+peak+press&rh=n%3A283155%2Cp_n_publication_date%3A1250226011"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-7 py-3.5 text-sm font-medium text-navy-500 border-2 border-navy-200 rounded-full hover:border-navy-500 hover:bg-navy-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Check New Releases
                </a>
              </div>

              {/* Author card */}
              <div className="mt-8 p-5 bg-white rounded-xl border border-cream-200 hover-lift">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center shrink-0">
                    <i className="ri-user-3-line text-gold-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-navy-900 mb-2">About the Author</h4>
                    <p className="text-sm text-navy-700 leading-relaxed">
                      {featured.authorBio}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BROWSE COLLECTION CTA ============ */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-navy-400/10 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute inset-0 animate-shimmer pointer-events-none"></div>
        <div ref={browseAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${browseAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs font-medium text-gold-400 uppercase tracking-[0.2em] mb-4">Looking for new reads?</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
              Browse Our <span className="text-gold-400">Collection</span>
            </h2>
            <p className="text-sm md:text-base text-cream-200 leading-relaxed mb-8 max-w-3xl mx-auto">
              Browse through our extensive catalog showcasing the diverse array of published books brought to you by Leavitt Peak Press. Explore this curated collection of literary works encompassing various genres, each crafted with meticulous attention to detail and bound to captivate readers of all interests.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.amazon.com/s?k=leavitt+peak+press"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
              >
                Browse All Books
              </a>
              <a
                href="/services"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Our Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ DISTRIBUTION MARQUEE ============ */}
      <DistributionMarquee />

      {/* ============ CATEGORY CARDS ============ */}
      <section className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={categoryAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${categoryAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-10">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Find Your Genre</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Explore by Category</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryCards.map((card, idx) => (
                <a
                  key={card.title}
                  href={card.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block relative bg-white rounded-2xl p-6 border border-cream-200 hover:border-gold-300 transition-all duration-500 hover:shadow-[0_8px_40px_rgba(196,154,58,0.12)] hover:-translate-y-2"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <div className="w-14 h-14 mb-5 flex items-center justify-center rounded-2xl bg-gradient-to-br from-gold-100 to-gold-50 transition-all duration-500 group-hover:from-gold-500 group-hover:to-gold-400 group-hover:scale-110 group-hover:rotate-6">
                    <img src={card.icon} alt={card.title} className="w-7 h-7 object-contain transition-all duration-500 group-hover:brightness-0 group-hover:invert" />
                  </div>
                  <h4 className="text-base font-semibold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors duration-300">
                    {card.title}
                  </h4>
                  <p className="text-sm text-navy-600 leading-relaxed">{card.description}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs font-medium text-gold-600 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    <span>Browse Category</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS SECTION ============ */}
      <section className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={testimonialAnim.ref} className={`max-w-4xl mx-auto animate-on-scroll ${testimonialAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-10">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">What Authors Say</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Trusted by Authors Worldwide</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
            </div>

            {/* Testimonial Carousel */}
            <div className="relative">
              <div className="flex items-center gap-4">
                <button
                  onClick={prevTestimonial}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white border border-cream-200 hover:border-gold-400 hover:bg-gold-50 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Previous testimonial"
                >
                  <i className="ri-arrow-left-s-line text-navy-700"></i>
                </button>

                <div className="flex-1 overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
                  >
                    {testimonials.map((t) => (
                      <div key={t.id} className="w-full flex-shrink-0 px-2">
                        <div className="bg-white rounded-2xl p-8 md:p-10 border border-cream-200">
                          <div className="flex justify-center mb-6">
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className="ri-star-fill text-gold-400 text-lg"></i>
                            ))}
                          </div>
                          <blockquote className="text-base md:text-lg text-navy-700 leading-relaxed text-center mb-8 italic">
                            &ldquo;{t.quote}&rdquo;
                          </blockquote>
                          <div className="flex items-center justify-center gap-4">
                            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-gold-300">
                              <img src={t.avatar} alt={t.author} className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-navy-900">{t.author}</p>
                              <p className="text-xs text-navy-500">{t.role}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={nextTestimonial}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white border border-cream-200 hover:border-gold-400 hover:bg-gold-50 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Next testimonial"
                >
                  <i className="ri-arrow-right-s-line text-navy-700"></i>
                </button>
              </div>

              {/* Dots */}
              <div className="flex items-center justify-center gap-2 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTestimonial(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      idx === currentTestimonial
                        ? 'bg-gold-500 w-8'
                        : 'bg-cream-300 hover:bg-cream-400'
                    }`}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  />
                ))}
              </div>

              {/* Mobile arrows */}
              <div className="flex md:hidden items-center justify-center gap-4 mt-4">
                <button onClick={prevTestimonial} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-cream-200 hover:scale-110 transition-transform">
                  <i className="ri-arrow-left-s-line text-navy-700"></i>
                </button>
                <button onClick={nextTestimonial} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-cream-200 hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-s-line text-navy-700"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PARTNER STATEMENT ============ */}
      <PartnerStatement />

      {/* ============ PUBLISHING PROCESS ============ */}
      <section className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={processAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${processAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-12">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">How We Work</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Your Publishing Journey</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
            </div>

            <div className="relative">
              {/* Connecting line */}
              <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-gold-200 via-gold-400 to-gold-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {publishingProcess.map((step, idx) => (
                  <div key={step.step} className="relative text-center group" style={{ animationDelay: `${idx * 0.1}s` }}>
                    {/* Step number circle */}
                    <div className="relative z-10 w-12 h-12 mx-auto mb-5 flex items-center justify-center rounded-full bg-white border-2 border-gold-400 text-gold-600 font-bold text-sm transition-all duration-500 group-hover:bg-gold-500 group-hover:text-white group-hover:border-gold-500 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(196,154,58,0.3)]">
                      {step.step}
                    </div>
                    <div className="w-10 h-10 mx-auto mb-4 flex items-center justify-center rounded-xl bg-gold-50 text-gold-500 transition-all duration-500 group-hover:bg-gold-100">
                      <i className={`${step.icon} text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-navy-900 mb-2">{step.title}</h4>
                    <p className="text-xs text-navy-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center mt-10">
              <a
                href="/services"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-navy-500 border-2 border-navy-200 rounded-full hover:border-navy-500 hover:bg-navy-50 transition-all duration-300 hover:scale-105"
              >
                Learn More About Our Services
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ VIDEO TRAILERS ============ */}
      <section className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 to-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={videoAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${videoAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
              <div>
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Watch & Discover</p>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-900">Author's Book Trailers</h2>
              </div>
              <a
                href="https://www.youtube.com/@leavittpeakpressllc"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-youtube-fill text-lg"></i>
                Visit Our YouTube Channel
              </a>
            </div>
            <p className="text-sm text-navy-700 leading-relaxed mb-8 max-w-4xl">
              Embark on an extraordinary cinematic voyage into the heart of our author's literary universe with the captivating book trailer meticulously crafted by the visionary team at Leavitt Peak Press. Through a seamless blend of breathtaking visuals, evocative music, and compelling narration, we breathe life into the pages of their book.
            </p>

            {/* Video Grid */}
            <div className="relative">
              <div className="flex items-center gap-4">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {videosLoading ? (
                    <>
                      <div className="overflow-hidden rounded-2xl aspect-video bg-cream-200 animate-pulse"></div>
                      <div className="overflow-hidden rounded-2xl aspect-video bg-cream-200 animate-pulse hidden sm:block"></div>
                      <div className="overflow-hidden rounded-2xl aspect-video bg-cream-200 animate-pulse hidden lg:block"></div>
                    </>
                  ) : (
                    displayVideos.map((video, idx) => (
                      <div key={video.id} className="group cursor-pointer hover-lift" onClick={() => setActiveVideo(video.videoId)} style={{ animationDelay: `${idx * 0.15}s` }}>
                        <div className="relative overflow-hidden rounded-2xl aspect-video shadow-md">
                          <img
                            src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-navy-900/20 group-hover:bg-navy-900/30 transition-colors duration-300 flex items-center justify-center">
                            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold-400/90 group-hover:bg-gold-400 group-hover:scale-110 transition-all duration-300 shadow-lg">
                              <i className="ri-play-fill text-navy-900 text-2xl ml-0.5"></i>
                            </div>
                          </div>
                        </div>
                        <p className="mt-3 text-sm font-medium text-navy-800 text-center truncate">{video.title}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {!videosLoading && !videosError && latestVideos.length > 0 && (
                <div className="flex items-center justify-center gap-2 mt-5">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs text-navy-500">Latest from YouTube — auto-updates</span>
                </div>
              )}
              {videosError && (
                <div className="text-center mt-6">
                  <p className="text-sm text-navy-500 mb-3">Unable to load videos right now.</p>
                  <a
                    href="https://www.youtube.com/@leavittpeakpressllc/videos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-navy-500 border-2 border-navy-200 rounded-full hover:border-navy-500 hover:bg-navy-50 transition-all duration-300"
                  >
                    <i className="ri-youtube-fill text-lg"></i>
                    Watch on YouTube
                  </a>
                </div>
              )}
              {!videosLoading && !videosError && latestVideos.length === 0 && (
                <div className="text-center mt-6">
                  <p className="text-sm text-navy-500 mb-3">No videos found. Check back soon!</p>
                  <a
                    href="https://www.youtube.com/@leavittpeakpressllc/videos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-navy-500 border-2 border-navy-200 rounded-full hover:border-navy-500 hover:bg-navy-50 transition-all duration-300"
                  >
                    <i className="ri-youtube-fill text-lg"></i>
                    Visit YouTube Channel
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-navy-950/90 flex items-center justify-center p-4 animate-fade-in-down" onClick={() => setActiveVideo(null)}>
          <div className="relative w-full max-w-4xl aspect-video animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <iframe
              className="w-full h-full rounded-xl"
              src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-gold-400 transition-colors duration-300"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* ============ RECOMMENDED BOOKS ============ */}
      <section className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={recommendAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${recommendAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-10">
              <div>
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Ready to explore?</p>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-900">Recommended Books</h2>
                {!bookLoading && latestBook && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-xs text-navy-500">Latest publication — auto-updates</span>
                  </div>
                )}
              </div>
              <a
                href="https://www.amazon.com/s?k=leavitt+peak+press"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Browse All
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Book Carousel */}
            <div className="relative">
              <div className="flex items-center gap-4">
                <button
                  onClick={prevBook}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-cream-100 hover:bg-gold-100 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-s-line text-navy-700"></i>
                </button>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recommendedBooks
                    .slice(currentBook * 3, currentBook * 3 + 3)
                    .map((book, idx) => (
                      <div key={book.id} className="bg-white rounded-2xl overflow-hidden border border-cream-200 hover:border-gold-200 hover:shadow-[0_12px_40px_rgba(196,154,58,0.1)] transition-all duration-500 hover:-translate-y-2 group" style={{ animationDelay: `${idx * 0.1}s` }}>
                        <div className="relative bg-gradient-to-br from-cream-100 to-cream-50 aspect-[3/4] overflow-hidden">
                          {book.cover ? (
                            <img
                              src={book.cover}
                              alt={book.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className="ri-book-open-line text-5xl text-cream-300"></i>
                            </div>
                          )}
                          <div className="absolute top-3 left-3">
                            <span className={`px-3 py-1 text-[10px] font-semibold rounded-full whitespace-nowrap ${
                              (book as any).isLatest
                                ? 'text-white bg-green-600/90 animate-pulse'
                                : 'text-navy-900 bg-gold-400/90'
                            }`}>
                              {(book as any).isLatest ? 'New Release' : 'Bestseller'}
                            </span>
                          </div>
                        </div>
                        <div className="p-5">
                          <h5 className="text-sm font-semibold text-navy-900 mb-1 line-clamp-2">{book.title}</h5>
                          {book.subtitle && (
                            <p className="text-xs text-gold-600 font-medium mb-1">{book.subtitle}</p>
                          )}
                          <p className="text-xs text-navy-500 mb-3">{book.author}</p>
                          {book.description && (
                            <p className="text-xs text-navy-600 line-clamp-2 mb-4 leading-relaxed">{book.description}</p>
                          )}
                          <a
                            href={(book as any).isLatest
                              ? `https://www.amazon.com/s?k=${encodeURIComponent(book.title + ' Leavitt Peak Press')}&i=stripbooks`
                              : book.link
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-full transition-all duration-300 ${
                              (book as any).isLatest
                                ? 'text-navy-900 bg-gold-400 hover:bg-gold-300'
                                : 'text-white bg-navy-500 hover:bg-navy-600'
                            }`}
                          >
                            {(book as any).isLatest ? 'View on Amazon' : 'Read More'}
                            <i className="ri-arrow-right-line"></i>
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
                <button
                  onClick={nextBook}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-cream-100 hover:bg-gold-100 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-s-line text-navy-700"></i>
                </button>
              </div>
              <div className="flex md:hidden items-center justify-center gap-4 mt-4">
                <button onClick={prevBook} className="w-8 h-8 flex items-center justify-center rounded-full bg-cream-100 hover:scale-110 transition-transform">
                  <i className="ri-arrow-left-s-line text-navy-700"></i>
                </button>
                <button onClick={nextBook} className="w-8 h-8 flex items-center justify-center rounded-full bg-cream-100 hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-s-line text-navy-700"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SPECIAL DEALS ============ */}
      <section className="w-full py-16 md:py-24 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Books.png"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950/80 via-transparent to-navy-950/80"></div>
        </div>
        {/* Decorative blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-navy-400/10 rounded-full blur-3xl"></div>
        <div ref={dealsAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${dealsAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/20 border border-gold-400/30 rounded-full mb-6">
              <i className="ri-sparkling-2-line text-gold-400 text-sm"></i>
              <span className="text-xs font-medium text-gold-300 uppercase tracking-widest">Limited Time Offers</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
              Check Out Our <span className="text-gold-400">Special Deals</span> on Books!
            </h2>
            <p className="text-sm md:text-base text-cream-200 leading-relaxed mb-8 max-w-2xl mx-auto">
              Discover amazing discounts on selected titles from our catalog. From captivating fiction to life-changing non-fiction, grab your next great read at an unbeatable price.
            </p>
            <a
              href="https://www.amazon.com/s?k=leavitt+peak+press&i=stripbooks&rh=n%3A283155%2Cp_72%3A1250221011"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
            >
              Check What's on Sale
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* ============ GALLERY ============ */}
      <section className="w-full py-14 md:py-20 bg-navy-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-gold-500/3 rounded-full blur-3xl"></div>
        <div className="w-full px-4 md:px-6 lg:px-8 relative z-10">
          <div ref={galleryAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${galleryAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-10">
              <p className="text-xs font-medium text-gold-400 uppercase tracking-[0.2em] mb-3">Our Reach</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Leavitt Peak Press <span className="text-gold-400">To The World</span>
              </h2>
              <p className="text-sm text-cream-200 leading-relaxed max-w-3xl mx-auto">
                At Leavitt Peak Press, we're not just a publishing house; we're your dedicated partner in literary success. We go above and beyond to ensure our authors' works receive the recognition they deserve. From participating in prestigious international book fairs to orchestrating captivating book signings and engaging directly with businesses and clients, we leave no stone unturned in our mission to help our authors achieve their publishing and marketing goals.
              </p>
            </div>

            {/* Gallery Carousel */}
            <div className="relative">
              <div className="flex items-center gap-4">
                <button
                  onClick={prevGallery}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-gold-500/30 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-s-line text-white"></i>
                </button>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {galleryImages
                    .slice(currentGallery * galleryPerPage, currentGallery * galleryPerPage + galleryPerPage)
                    .map((img, idx) => (
                      <div key={idx} className="overflow-hidden rounded-2xl group relative aspect-[4/3]">
                        <img
                          src={img}
                          alt={`Gallery image ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                          <span className="text-xs font-medium text-white bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">Book Fair Event</span>
                        </div>
                      </div>
                    ))}
                </div>
                <button
                  onClick={nextGallery}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-gold-500/30 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-s-line text-white"></i>
                </button>
              </div>
              {/* Dots indicator */}
              <div className="flex items-center justify-center gap-2 mt-6">
                {Array.from({ length: gallerySlides }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentGallery(idx)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      idx === currentGallery ? 'bg-gold-400 w-6' : 'bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
              <div className="flex md:hidden items-center justify-center gap-4 mt-4">
                <button onClick={prevGallery} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:scale-110 transition-transform">
                  <i className="ri-arrow-left-s-line text-white"></i>
                </button>
                <button onClick={nextGallery} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-s-line text-white"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BOOK ENGAGEMENTS ============ */}
      <section className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 to-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={engageAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${engageAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              {/* Left - Upcoming Event Images */}
              <div className="flex-1 flex flex-col sm:flex-row gap-4 w-full">
                {upcomingEvents.map((event, idx) => (
                  <a
                    key={event.id}
                    href="/events"
                    className="flex-1 overflow-hidden rounded-2xl group hover-lift shadow-md"
                  >
                    <div className="relative aspect-[4/3]">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 text-[10px] font-semibold text-white bg-gold-500 rounded-full uppercase tracking-wider whitespace-nowrap">
                          {event.status}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-xs font-semibold text-white bg-gold-500/90 rounded-full px-3 py-1 whitespace-nowrap">{event.title}</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              {/* Right - Text & Events List */}
              <div className="flex-1">
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Ready to showcase your book?</p>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">Join These Book Engagements</h2>
                <div className="w-16 h-1 bg-gold-400 rounded-full mb-6"></div>

                {/* Upcoming Events Mini List */}
                <div className="space-y-3 mb-5">
                  {upcomingEvents.map((event) => (
                    <div key={event.id} className="flex items-start gap-3 p-3.5 bg-white rounded-xl border border-cream-200 hover:border-gold-200 transition-all duration-300">
                      <div className="w-10 h-10 rounded-xl bg-gold-50 flex items-center justify-center shrink-0">
                        <i className={`${event.icon} text-gold-600`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold text-white rounded-full uppercase tracking-wider whitespace-nowrap ${event.status === 'featured' ? 'bg-red-600' : 'bg-gold-500'}`}>{event.status === 'featured' ? 'Featured' : 'Upcoming'}</span>
                          <h4 className="text-sm font-semibold text-navy-900 truncate">{event.title}</h4>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-navy-500">
                          <span className="flex items-center gap-1 whitespace-nowrap"><i className="ri-calendar-line text-gold-500"></i>{event.date}</span>
                          <span className="flex items-center gap-1 whitespace-nowrap"><i className="ri-map-pin-line text-gold-500"></i>{event.location.split(',')[0]}</span>
                          {'videoId' in event && (event as any).videoId && (
                            <a
                              href={`https://youtu.be/${(event as any).videoId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-red-600 hover:text-red-700 font-medium whitespace-nowrap ml-auto"
                            >
                              <i className="ri-youtube-fill"></i>
                              Watch
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent Events Badge */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-xs font-medium text-navy-500">Recent:</span>
                  {pastEvents.slice(0, 2).map((event) => (
                    <a
                      key={event.id}
                      href="/events"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cream-100 hover:bg-cream-200 rounded-full text-xs text-navy-600 transition-colors duration-300 whitespace-nowrap"
                    >
                      <i className="ri-check-double-line text-gold-500 text-xs"></i>
                      {event.title}
                    </a>
                  ))}
                </div>

                <p className="text-sm text-navy-700 leading-relaxed mb-4">
                  Catch our brand-new <strong className="text-navy-900">Kurt Francis author showcase</strong>, produced with the Invigorator — streaming now on YouTube! And for authors looking to go global, secure your spot at the world-renowned <strong className="text-navy-900">Frankfurt Book Fair</strong> this October. Connect with publishers, agents, and distributors from over 100 countries at the exclusive Leavitt Peak Press booth.
                </p>

                <div className="flex items-center gap-3 p-4 bg-gold-50 border border-gold-200 rounded-xl mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold-500 flex items-center justify-center shrink-0">
                    <i className="ri-megaphone-line text-white"></i>
                  </div>
                  <p className="text-sm text-navy-800 font-medium">
                    <strong>Two ways to engage</strong> — watch the Kurt Francis showcase now, or reserve your Frankfurt booth before slots fill up!
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <a
                    href="/events"
                    className="w-full sm:w-auto px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    View All Events
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                  <a
                    href="/contact-us"
                    className="w-full sm:w-auto px-7 py-3.5 text-sm font-medium text-navy-500 border-2 border-navy-200 rounded-full hover:border-navy-500 hover:bg-navy-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    Contact Us
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BEST SELLERS ============ */}
      <section className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={bestsellerAnim.ref} className={`max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16 animate-on-scroll ${bestsellerAnim.isVisible ? 'animate-visible' : ''}`}>
            {/* Book Stack */}
            <div className="flex-1 flex items-center justify-center order-2 lg:order-1">
              <div className="relative flex items-end justify-center gap-4 h-80">
                {/* Background glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gold-200/30 rounded-full blur-3xl"></div>
                {/* Book 1 - left tilted */}
                <div className="w-36 md:w-44 relative z-0 -rotate-12 animate-float" style={{ marginBottom: '20px' }}>
                  <img
                    src={books[0].cover}
                    alt={books[0].title}
                    className="w-full h-auto rounded-lg shadow-xl transition-transform duration-500 hover:scale-105 hover:rotate-0 hover:z-20"
                  />
                </div>
                {/* Book 2 - center elevated */}
                <div className="w-40 md:w-48 relative z-20 animate-float-delayed" style={{ marginBottom: '50px' }}>
                  <img
                    src={books[1].cover}
                    alt={books[1].title}
                    className="w-full h-auto rounded-lg shadow-2xl shadow-navy-900/20 transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute -top-3 -right-3 w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <span className="text-xs font-bold text-white">#1</span>
                  </div>
                </div>
                {/* Book 3 - right tilted */}
                <div className="w-36 md:w-44 relative z-0 rotate-12 animate-float-slow" style={{ marginBottom: '20px' }}>
                  <img
                    src={books[2].cover}
                    alt={books[2].title}
                    className="w-full h-auto rounded-lg shadow-xl transition-transform duration-500 hover:scale-105 hover:rotate-0 hover:z-20"
                  />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 order-1 lg:order-2">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Top Picks</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4">Discover Our Best Sellers</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mb-6"></div>
              <p className="text-sm md:text-base text-navy-700 leading-relaxed mb-6">
                Meet the visionary authors of Leavitt Peak Press, whose literary prowess has propelled their works to remarkable success in the market. Through their dedication, creativity, and profound storytelling, they've captured the hearts and minds of readers worldwide, achieving unparalleled sales and acclaim. Join us in celebrating these exceptional talents whose contributions enrich and inspire the literary landscape, setting new standards for excellence in publishing.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <a
                  href="https://www.amazon.com/s?k=leavitt+peak+press&i=stripbooks&rh=n%3A283155%2Cp_72%3A1250221011"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Get the Book
                </a>
                <a
                  href="/about-us"
                  className="w-full sm:w-auto px-7 py-3.5 text-sm font-medium text-navy-500 border-2 border-navy-200 rounded-full hover:border-navy-500 hover:bg-navy-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Meet Our Authors
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PAYMENT CTA ============ */}
      <section className="w-full py-12 bg-gradient-to-r from-navy-800 via-navy-700 to-navy-800 relative overflow-hidden">
        <div className="absolute inset-0 animate-shimmer pointer-events-none"></div>
        <div ref={paymentAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${paymentAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gold-500/20 flex items-center justify-center shrink-0">
                <i className="ri-secure-payment-line text-gold-400 text-xl"></i>
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Need to Pay for Services?</h2>
                <p className="text-sm text-cream-300 mt-1">Fast, secure, and hassle-free payment processing</p>
              </div>
            </div>
            <a
              href="https://www.paypal.com/ncp/payment/3C6D5Z8CZQU2S"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
            >
              Pay Now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}